import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, Sticker } from 'lucide-react';
import { API } from '../../config/api';
import { useTranslation } from '../../i18n';

const StickerPanel = ({ onAddSticker, onClose, searchQuery: controlledQuery, onSearchQueryChange }) => {
  const { t } = useTranslation();
  const [internalQuery, setInternalQuery] = useState(controlledQuery || '');
  const searchQuery = controlledQuery !== undefined ? controlledQuery : internalQuery;
  const setSearchQuery = onSearchQueryChange || setInternalQuery;
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchStickers(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStickers = async (search = '') => {
    setLoading(true);
    try {
      const url = search
        ? `${API.STICKERS}?search=${encodeURIComponent(search)}&limit=500`
        : `${API.STICKERS}?limit=500`;
      const res = await fetch(url);
      const data = await res.json();
      setStickers(data.stickers || []);
    } catch (error) {
      console.error('Error fetching stickers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        fetchStickers(value);
      }
    }, 300);
  };

  const handleSelectSticker = (sticker) => {
    // Load image to get original dimensions
    const img = new window.Image();
    img.onload = () => {
      onAddSticker({
        src: sticker.file_path,
        originalWidth: img.naturalWidth,
        originalHeight: img.naturalHeight
      });
      onClose();
    };
    img.onerror = () => {
      // Fallback if image fails to load - use src only
      onAddSticker({ src: sticker.file_path });
      onClose();
    };
    img.src = `${API.BASE}${sticker.file_path}`;
  };

  return (
    <div
      className="bg-white rounded-xl shadow-xl border p-3 w-[300px]"
      style={{ maxHeight: '400px' }}
    >
      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('editor.stickerPanel.searchPlaceholder')}
          className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A4D25] focus:border-transparent"
          autoFocus
        />
        {searchQuery && (
          <button
            onClick={() => handleSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
          >
            <X size={14} className="text-gray-400" />
          </button>
        )}
      </div>

      {/* Sticker Grid */}
      <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin" style={{ color: '#2A4D25' }} />
          </div>
        ) : stickers.length > 0 ? (
          <div className="grid grid-cols-4 gap-2">
            {stickers.map((sticker) => (
              <button
                key={sticker.id}
                onClick={() => handleSelectSticker(sticker)}
                className="rounded-lg border-2 border-transparent hover:border-[#2A4D25] transition hover:bg-[#2A4D25]/10"
                title={sticker.name}
              >
                <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden">
                  <img
                    src={`${API.BASE}${sticker.file_path}`}
                    alt={sticker.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Sticker size={32} className="mb-2 opacity-50" />
            <p className="text-sm">
              {searchQuery ? t('editor.stickerPanel.notFound') : t('editor.stickerPanel.empty')}
            </p>
            {!searchQuery && (
              <p className="text-xs mt-1">{t('editor.stickerPanel.addInAdmin')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StickerPanel;
